'use client'
import {type ReactNode} from 'react'
import {type OmitStrict} from 'type-zoo'
import {HgArticleHeroHigh} from '~/design-system/hg/modules/HgArticleHero/components/HgArticleHeroHigh'
import {ARTICLE_HERO_BUTTON_CONTAINER_ID} from '~/design-system/hg/modules/HgArticleHero/components/HgArticleHeroHigh/HgArticleHeroHighHorizontal'
import HgArticleHeroLow from '~/design-system/hg/modules/HgArticleHero/components/HgArticleHeroLow'
import HgArticleHeroMedium from '~/design-system/hg/modules/HgArticleHero/components/HgArticleHeroMedium'
import {type Theme, type AuraColorToken} from '~/design-system/hg/tokens/colors'
import {safeUnreachableCase} from '~/utils/unreachableCase'
import Asset from '../../Asset'
import {DatoThemeSelector} from '../../DatoThemeSelector'
import {DatoThemeSwitcher} from '../../DatoThemeSwitcher'
import {type DatoAnimationProps} from '../../DatoVideo'
import {
  type ArticleHeroFragment,
  type ArticleHeroLowWithAssetFragment,
  type ArticleHeroMediumWithAssetFragment,
  type TopicFragment,
} from './_generated/fragment.graphql'

type ArticleHeroProps = {
  data?: ArticleHeroFragment
  topic: TopicFragment
  title: string
  subheading?: ReactNode
}

export default function ArticleHero({
  data,
  topic,
  title,
  subheading,
}: ArticleHeroProps) {
  if (!data) {
    return null
  }

  const sharedProps = {
    heading: title,
    subheading,
    tagText: topic.name,
  }

  const sharedHighProps = {
    ...sharedProps,
    level: 'high',
  } as const

  const sharedMediumProps = {
    ...sharedProps,
    level: 'medium',
  } as const

  const sharedLowProps = {
    ...sharedProps,
    level: 'low',
  } as const

  const maxSizeImageProps = {
    imgStyle: {
      objectFit: 'cover',
      height: '100%',
      maxWidth: '100%',
    } as const,
    priority: true,
  }

  const getMediumOrLowHeroAsset = (
    assetType: 'horizontal' | 'square' | 'vertical',
    variant: ArticleHeroMediumWithAssetFragment | ArticleHeroLowWithAssetFragment
  ) => {
    switch (assetType) {
      case 'horizontal':
        return (
          <Asset
            data={variant.horizontalAsset}
            aspectRatio="16:9"
            datoImageProps={maxSizeImageProps}
          />
        )
      case 'square':
        return (
          <Asset
            data={variant.squareAsset}
            aspectRatio="1:1"
            datoImageProps={maxSizeImageProps}
          />
        )
      case 'vertical':
        return (
          <Asset
            data={variant.verticalAsset}
            aspectRatio="4:5"
            datoImageProps={maxSizeImageProps}
          />
        )
    }
  }

  function renderContent() {
    if (!data) {
      return null
    }
    switch (data.variant.__typename) {
      case 'ArticleHeroHighHorizontalComponentRecord': {
        const animationProps: OmitStrict<
          DatoAnimationProps,
          'aspectRatio' | 'data'
        > = {
          buttonContainerId: ARTICLE_HERO_BUTTON_CONTAINER_ID,
          buttonVariant: 'frosted',
          loop: true,
          classNames: {
            video: 'h-screen',
          },
          autoplayVisibilityThreshold: 0.1,
          controlsTheme: (data.theme?.name ?? 'neutral') as Theme,
        }
        return (
          <HgArticleHeroHigh
            {...sharedHighProps}
            assetType="horizontal"
            assetSixteenNineRatio={
              <Asset
                data={data.variant.sixteenNineAsset}
                datoImageProps={maxSizeImageProps}
                datoAnimationProps={animationProps}
                aspectRatio="16:9"
              />
            }
            assetOneOneRatio={
              data.variant.oneOneAsset && (
                <Asset
                  data={data.variant.oneOneAsset}
                  aspectRatio="1:1"
                  datoImageProps={maxSizeImageProps}
                  datoAnimationProps={animationProps}
                />
              )
            }
            assetNineSixteenRatio={
              data.variant.nineSixteenAsset && (
                <Asset
                  data={data.variant.nineSixteenAsset}
                  aspectRatio="9:16"
                  datoImageProps={maxSizeImageProps}
                  datoAnimationProps={animationProps}
                />
              )
            }
          />
        )
      }
      case 'ArticleHeroHighVerticalComponentRecord':
        return (
          <HgArticleHeroHigh
            {...sharedHighProps}
            assetType="vertical"
            assetFourFiveRatio={
              <Asset
                data={data.variant.fourFiveAsset}
                aspectRatio="4:5"
                datoImageProps={maxSizeImageProps}
                datoAnimationProps={{classNames: {video: 'lg:h-screen'}}}
              />
            }
            assetOneOneRatio={
              data.variant.oneOneAsset && (
                <Asset
                  data={data.variant.oneOneAsset}
                  aspectRatio="1:1"
                  datoImageProps={maxSizeImageProps}
                />
              )
            }
            backgroundColorToken={
              data.variant.highVerticalBackgroundColor as AuraColorToken
            }
          />
        )
      case 'ArticleHeroHighTextOnlyComponentRecord':
        return (
          <HgArticleHeroHigh
            {...sharedHighProps}
            assetType="none"
            backgroundColorToken={
              data.variant.highTextOnlyBackgroundColor as AuraColorToken
            }
          />
        )
      case 'ArticleHeroMediumWithAssetComponentRecord': {
        const assetType = data.variant.assetType as
          | 'horizontal'
          | 'square'
          | 'vertical'
        const asset = getMediumOrLowHeroAsset(assetType, data.variant)
        return (
          <HgArticleHeroMedium
            {...sharedMediumProps}
            assetType={assetType}
            asset={asset}
            backgroundColorToken={
              data.variant.mediumWithAssetBackgroundColor as AuraColorToken
            }
          />
        )
      }
      case 'ArticleHeroMediumTextOnlyComponentRecord':
        return (
          <HgArticleHeroMedium
            {...sharedMediumProps}
            assetType="none"
            backgroundColorToken={
              data.variant.mediumTextOnlyBackgroundColor as AuraColorToken
            }
          />
        )
      case 'ArticleHeroLowWithAssetComponentRecord': {
        const assetType = data.variant.assetType as
          | 'horizontal'
          | 'square'
          | 'vertical'
        const asset = getMediumOrLowHeroAsset(assetType, data.variant)

        return (
          <HgArticleHeroLow
            {...sharedLowProps}
            assetType={assetType}
            asset={asset}
            backgroundColorToken={
              data.variant.lowWithAssetBackgroundColor as AuraColorToken
            }
          />
        )
      }
      case 'ArticleHeroLowTextOnlyComponentRecord':
        return (
          <HgArticleHeroLow
            {...sharedLowProps}
            assetType="none"
            backgroundColorToken={
              data.variant.lowTextOnlyBackgroundColor as AuraColorToken
            }
          />
        )
      default:
        return safeUnreachableCase(data.variant, null)
    }
  }

  return (
    <DatoThemeSwitcher
      theme={data.theme ?? {name: 'neutral', id: ''}}
      applyGlobally={false}
    >
      <div>
        <DatoThemeSelector theme={data.theme ?? {name: 'neutral', id: ''}}>
          <section data-section="article-hero">{renderContent()}</section>
        </DatoThemeSelector>
      </div>
    </DatoThemeSwitcher>
  )
}
